/**
 * Org Service (26/10/2026): hooks del directorio /org. Lecturas del router `org.*`
 * (schema `org` en el Aurora del Brain). El acceso NO se pregunta acá para el menú: viene
 * en `profile.getVisibility` (pieza `org`); `useOrgAccess` es solo para las páginas.
 */
import { trpc } from '@/lib/trpc';

const ACCESS_OPTS = { retry: false, staleTime: 5 * 60_000 } as const;
const READ_OPTS = { staleTime: 60_000 } as const;

export function useOrgAccess() {
  return trpc.org.checkAccess.useQuery(undefined, ACCESS_OPTS);
}
export function useOrgMe() {
  return trpc.org.me.useQuery(undefined, READ_OPTS);
}
export function useOrgPeople() {
  return trpc.org.people.useQuery(undefined, { staleTime: 5 * 60_000 });
}
export function useOrgPerson(email: string | undefined) {
  return trpc.org.person.useQuery({ email: email ?? '' }, { ...READ_OPTS, enabled: !!email, retry: false });
}
export function useOrgSolutions() {
  return trpc.org.solutions.useQuery(undefined, READ_OPTS);
}
export function useOrgSolution(slug: string | undefined) {
  return trpc.org.solution.useQuery({ slug: slug ?? '' }, { ...READ_OPTS, enabled: !!slug, retry: false });
}
export function useOrgClients() {
  return trpc.org.clients.useQuery(undefined, READ_OPTS);
}
export function useOrgClient(clientId: string | undefined) {
  return trpc.org.client.useQuery({ clientId: clientId ?? '' }, { ...READ_OPTS, enabled: !!clientId, retry: false });
}
export function useOrgTeam(enabled: boolean, leaderEmail?: string) {
  return trpc.org.team.useQuery(leaderEmail ? { leaderEmail } : undefined, { ...READ_OPTS, enabled });
}
export function useOrgHealth(enabled: boolean) {
  return trpc.org.health.useQuery(undefined, { staleTime: 30_000, enabled, retry: false });
}
export function useOrgGovernance(enabled = true) {
  return trpc.org.governance.getGovernance.useQuery(undefined, { retry: false, enabled });
}
export function useOrgCommunityPicker(enabled = true) {
  return trpc.org.governance.listCommunityForPicker.useQuery(undefined, { staleTime: 5 * 60_000, retry: false, enabled });
}
export function useSaveOrgGovernance() {
  const utils = trpc.useUtils();
  return trpc.org.governance.setGovernance.useMutation({
    onSuccess: () => {
      utils.org.governance.getGovernance.invalidate();
      utils.org.checkAccess.invalidate();
      utils.profile.getVisibility.invalidate();
    },
  });
}

export function useOrgClientsAll(enabled = true) {
  return trpc.org.clientsAll.useQuery(undefined, { staleTime: 5 * 60_000, enabled });
}

/** Escrituras del manager (T3): confirmar, cerrar, reabrir, corregir, declarar. Invalidan equipo, ficha y clientes. */
export function useOrgManagerMutations() {
  const utils = trpc.useUtils();
  const onSuccess = () => {
    utils.org.team.invalidate();
    utils.org.me.invalidate();
    utils.org.clients.invalidate();
    utils.org.person.invalidate();
    utils.org.client.invalidate();
    utils.org.solution.invalidate();
  };
  const confirm = trpc.org.confirm.useMutation({ onSuccess });
  const close = trpc.org.close.useMutation({ onSuccess });
  const reopen = trpc.org.reopen.useMutation({ onSuccess });
  const correct = trpc.org.correct.useMutation({ onSuccess });
  const declare = trpc.org.declare.useMutation({ onSuccess });
  const busy = confirm.isPending || close.isPending || reopen.isPending || correct.isPending || declare.isPending;
  return { confirm, close, reopen, correct, declare, busy };
}

/** Agente de asignaciones (T3b): propone sugeridas; al volver, el panel de pendientes se refresca. */
export function useOrgAgentAsk() {
  const utils = trpc.useUtils();
  return trpc.org.agent.ask.useMutation({ onSuccess: () => { utils.org.team.invalidate(); } });
}

// ─── T5: catálogo, políticas y accesos ───────────────────────────────────────
export function useOrgTools() {
  return trpc.org.tools.list.useQuery(undefined, READ_OPTS);
}
export function useOrgPolicies(filter?: { solutionSlug?: string }) {
  return trpc.org.policies.list.useQuery(filter?.solutionSlug ? { solutionSlug: filter.solutionSlug } : undefined, READ_OPTS);
}
/** Accesos de una persona (lo que piden las políticas vs lo que tiene). FORBIDDEN = no es tu reporte: se trata aparte. */
export function useOrgPersonAccess(email: string | undefined) {
  return trpc.org.access.forPerson.useQuery({ email: email ?? '' }, { ...READ_OPTS, enabled: !!email, retry: false });
}
export function useOrgCatalogMutations() {
  const utils = trpc.useUtils();
  const onSuccess = () => {
    utils.org.tools.list.invalidate();
    utils.org.policies.list.invalidate();
    utils.org.access.forPerson.invalidate();
  };
  const toolUpsert = trpc.org.tools.upsert.useMutation({ onSuccess });
  const toolImport = trpc.org.tools.importMany.useMutation({ onSuccess });
  const toolRemove = trpc.org.tools.remove.useMutation({ onSuccess });
  const policyCreate = trpc.org.policies.create.useMutation({ onSuccess });
  const policyUpdate = trpc.org.policies.update.useMutation({ onSuccess });
  const policyRemove = trpc.org.policies.remove.useMutation({ onSuccess });
  const policyImport = trpc.org.policies.importMany.useMutation({ onSuccess });
  const accessRecord = trpc.org.access.record.useMutation({ onSuccess });
  const busy = [toolUpsert, toolImport, toolRemove, policyCreate, policyUpdate, policyRemove, policyImport, accessRecord].some((m) => m.isPending);
  return { toolUpsert, toolImport, toolRemove, policyCreate, policyUpdate, policyRemove, policyImport, accessRecord, busy };
}
